import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import Header from '../../components/Header'
import SearchForm from '../../components/SearchForm'

const serviceTabs = [
  {
    id: 'transfers',
    label: 'Transfers',
    mobileLabel: 'Transfers',
    icon: 'transfers' as const,
  },
  {
    id: 'hourly',
    label: 'Hourly driver',
    mobileLabel: 'Hourly',
    icon: 'clock' as const,
  },
  {
    id: 'daytrips',
    label: 'Day Trips',
    mobileLabel: 'Day Trips',
    icon: 'daytrips' as const,
  },
] as const

function HeroBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* Smooth left-to-right hero gradient */}
      <div className="absolute inset-0 z-0 bg-page-gradient" />

      {/* Building layer */}
      <motion.img
        initial={{ y: 200, opacity: 0 }}
        animate={{ y: 0, opacity: 0.95 }}
        transition={{
          duration: 1,
          ease: 'easeOut',
          delay: 0.1,
        }}
        src="/assets/images/hero-bg-main.png"
        alt=""
        className="absolute bottom-[0vh] right-[-22vw] z-[1] h-[clamp(600px,90vh,690px)] w-[clamp(790px,88vw,1420px)] max-w-none object-cover object-center"
      />

      {/* Cloud behind flight */}
      <motion.img
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: 1,
          ease: 'easeOut',
          delay: 0.3,
        }}
        src="/assets/images/hero-side.png"
        alt=""
        className="absolute right-[-0vw] top-[clamp(155px,35vh,300px)] z-[0] hidden w-[clamp(185px,25vw,459px)] max-w-none object-contain xl:block"
      />

      {/* Flight */}
      <motion.img
        initial={{ x: 150, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: 1,
          ease: 'easeOut',
          delay: 0.5,
        }}
        src="/assets/images/hero-badge.png"
        alt=""
        className="absolute right-[-2vw] top-[clamp(96px,22vh,190px)] z-[5] hidden w-[clamp(120px,16vw,245px)] max-w-none object-contain md:block"
      />

      {/* Cars — stay behind search bar */}
      <motion.img
        initial={{ x: -150, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: 1,
          ease: 'easeOut',
          delay: 0.4,
        }}
        src="/assets/images/hero-car.png"
        alt=""
        className="absolute bottom-[-18vh] left-[-3vw] z-[2] hidden w-[clamp(420px,49vw,780px)] max-w-none object-contain md:block"
      />
    </div>
  )
}

type ServiceTabId = (typeof serviceTabs)[number]['id']

type ServiceTabsProps = {
  active: ServiceTabId
  onChange: (id: ServiceTabId) => void
}

function ServiceTabsComponent({
  active,
  onChange,
}: ServiceTabsProps) {
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
  })

  const tabsRowRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])

  const iconMap = {
    transfers: '/assets/images/tab-transfers.png',
    clock: '/assets/images/icon-clock.png',
    daytrips: '/assets/images/tab-daytrips.png',
  }

  useEffect(() => {
    const activeIndex = serviceTabs.findIndex(
      (tab) => tab.id === active
    )

    const activeButton = buttonRefs.current[activeIndex]
    const tabsRow = tabsRowRef.current

    if (!activeButton || !tabsRow) return

    const rowRect = tabsRow.getBoundingClientRect()
    const buttonRect = activeButton.getBoundingClientRect()

    setIndicatorStyle({
      left: buttonRect.left - rowRect.left,
      width: buttonRect.width,
    })
  }, [active])

  return (
    <div
      className="
        mt-[75px]
        w-full
        rounded-t-[20px]
        bg-white
        pt-1

        md:mt-[clamp(14px,2.8vh,24px)]
        md:w-fit
        md:max-w-full
        md:rounded-none
        md:bg-transparent
        md:pt-0
      "
    >
      <div
        ref={tabsRowRef}
        className="
          grid
          h-[50px]
          w-full
          grid-cols-3
          items-stretch

          md:flex
          md:h-auto
          md:flex-wrap
          md:items-center
          md:gap-[clamp(14px,3vw,40px)]
        "
      >
        {serviceTabs.map((tab, index) => {
          const isActive = tab.id === active

          return (
            <button
              key={tab.id}
              ref={(element) => {
                buttonRefs.current[index] = element
              }}
              type="button"
              onClick={() => onChange(tab.id)}
              className="
                group
                flex
                items-center
                justify-center
                gap-2
                px-2

                transition-transform
                duration-200

                hover:-translate-y-0.5

                md:justify-start
                md:px-0
                md:gap-[clamp(4px,0.55vw,8px)]
              "
            >
              <img
                src={iconMap[tab.icon]}
                alt=""
                className="
                  h-[18px]
                  w-[18px]
                  object-contain

                  transition-transform
                  duration-200

                  group-hover:scale-110

                  md:h-[clamp(10px,1.45vw,20px)]
                  md:w-[clamp(10px,1.45vw,20px)]
                "
              />

              <span
                className={`
                  whitespace-nowrap
                  font-poppins
                  text-[14px]
                  font-medium
                  leading-none

                  transition-colors
                  duration-200

                  group-hover:text-brand-orange

                  md:text-[clamp(10px,1.05vw,16px)]

                  ${
                    isActive
                      ? 'text-brand-orange md:text-black'
                      : 'text-[#8B8B8B] md:text-black'
                  }
                `}
              >
                <span className="md:hidden">
                  {tab.mobileLabel}
                </span>

                <span className="hidden md:inline">
                  {tab.label}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <div
        className="
          relative
          h-[1.5px]
          w-full
          bg-[#ECECEC]

          md:mt-[clamp(10px,1.7vh,16px)]
          md:h-[2px]
          md:bg-[#C6C6C6]
        "
      >
        <div
          className="
            absolute
            top-0
            h-[2px]
            rounded-full
            bg-brand-orange
            transition-all
            duration-300
          "
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
      </div>
    </div>
  )
}

export default function Hero() {
  const [activeService, setActiveService] =
    useState<ServiceTabId>('transfers')

  useEffect(() => {
    const handleSetTab = (e: Event) => {
      const customEvent = e as CustomEvent<ServiceTabId>
      if (
        customEvent.detail &&
        ['transfers', 'hourly', 'daytrips'].includes(customEvent.detail)
      ) {
        setActiveService(customEvent.detail)
      }
    }

    const checkHash = () => {
      const search = new URLSearchParams(window.location.search)
      if (
        search.get('serviceType') === 'skitransfers' ||
        window.location.hash === '#transfers'
      ) {
        setActiveService('transfers')
      }
    }

    window.addEventListener('salzburg-set-hero-tab', handleSetTab as EventListener)
    window.addEventListener('hashchange', checkHash)
    checkHash()

    return () => {
      window.removeEventListener('salzburg-set-hero-tab', handleSetTab as EventListener)
      window.removeEventListener('hashchange', checkHash)
    }
  }, [])

  return (
    <section className="relative z-20 min-h-[100svh] overflow-visible bg-page-gradient">
      <Header absolute />

      <div className="relative z-10 flex min-h-[100svh] w-full flex-col">
        <HeroBackground />

        <div className="relative z-20 flex flex-1 items-center px-[18px] pb-[28px] pt-[clamp(126px,20vh,220px)] md:px-[clamp(24px,4.8vw,72px)] md:pb-[clamp(56px,14vh,130px)]">
          <div className="mx-auto flex w-full max-w-[min(100%,1400px)] flex-col gap-0 md:gap-[clamp(24px,5vh,44px)]">

            <div className="max-w-[min(100%,760px)]">
              <h1 className="font-host text-[clamp(28px,6vw,36px)] font-bold leading-[1.08] tracking-[-0.035em] text-text-primary md:whitespace-nowrap lg:whitespace-normal xl:whitespace-nowrap">
                <span>Private Transfers</span>
                <span> &amp; </span>
                <span>Scenic</span>
                <span className="text-brand-green">
                  {' '}
                  Day Trips
                </span>
                <span> from Salzburg</span>
              </h1>

              <p className="mt-[clamp(5px,1.1vh,12px)] font-poppins text-[clamp(14px,4vw,18px)] font-medium leading-[1.45] tracking-[-0.02em] text-[#636363]">
                Travel city-to-city, by the hour or take day trips.
              </p>

              <ServiceTabsComponent
                active={activeService}
                onChange={setActiveService}
              />
            </div>

            <div id="transfers" className="relative z-30 w-full max-w-[min(100%,1380px)] scroll-mt-28">
              <SearchForm serviceType={activeService} />
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}